import { z } from "zod";
import { deletedResponseSchema, emailSchema, firestoreDocument, norwegianBankAccountSchema, phoneSchema, sentResponseSchema, updatedResponseSchema, userFlagSchema, } from "./common.js";
export const grantFlagSchema = z.object({
    uid: z.string().min(1),
    flags: z.array(userFlagSchema),
});
export const inviteAdminSchema = z.object({
    email: emailSchema,
    flags: z.array(userFlagSchema).min(1),
    manifestIds: z.array(z.string().min(1).max(64)).max(10).optional(),
});
export const verifyIdSchema = z.object({
    method: z.enum(["PASSPORT", "DRIVER_LICENSE", "NATIONAL_ID_CARD", "OTHER"]),
    documentNumber: z.string().min(1).max(60),
    documentExpiry: z.number().int().positive().optional(),
    documentCopyStored: z.boolean().default(false),
    documentCopyLocation: z.string().max(200).optional(),
    notes: z.string().max(500).optional(),
});
export const clearIdVerificationSchema = z
    .object({ reason: z.string().max(500).optional() })
    .default({});
export const createCustomerNoteSchema = z.object({
    content: z.string().min(1).max(2000),
    pinned: z.boolean().optional(),
});
export const updateCustomerProfileSchema = z.object({
    name: z.string().min(1).max(120).nullable().optional(),
    phone: phoneSchema.nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    bankAccountNumber: norwegianBankAccountSchema.nullable().optional(),
});
export const createWalkinCustomerSchema = z.object({
    name: z.string().min(1).max(120),
    phone: phoneSchema,
    email: emailSchema.optional(),
    address: z.string().max(500).optional(),
    bankAccountNumber: norwegianBankAccountSchema.optional(),
    kontoEier: z.string().max(120).optional(),
});
export const mergeCustomersSchema = z
    .object({
    sourceUid: z.string().min(1).max(128),
    targetUid: z.string().min(1).max(128),
})
    .refine((v) => v.sourceUid !== v.targetUid, "Kilde og mål må være forskjellige");
export const updateProfileSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    phone: phoneSchema.optional(),
    email: emailSchema.optional(),
    address: z.string().max(500).optional(),
    postalCode: z
        .string()
        .regex(/^\d{4}$/, "Bruk fire siffer")
        .optional(),
    city: z.string().max(120).optional(),
    bankAccountNumber: norwegianBankAccountSchema.optional(),
    kontoEier: z.string().max(120).optional(),
    dateOfBirth: z
        .number()
        .int()
        .refine((v) => v < Date.now(), "Fødselsdato kan ikke være i fremtiden")
        .refine((v) => v > Date.now() - 130 * 365 * 24 * 60 * 60 * 1000, "Fødselsdato virker ikke realistisk")
        .optional(),
    marketingConsent: z.boolean().optional(),
});
export const verifyEmailConfirmSchema = z.object({
    token: z.string().min(16).max(128),
});
export const vippsPrefillConsumeSchema = z.object({
    ticket: z.string().min(8).max(200),
});
export const sendPasswordResetSchema = z.object({
    email: z.string().email(),
});
export const prepareStaffInviteSchema = z.object({
    oobCode: z.string().min(1),
    apiKey: z.string().min(1),
});
export const completeStaffInviteSchema = z.object({
    ticket: z.string().min(1),
});
export const bootstrapSystemAdminResponseSchema = z.object({ bootstrapped: z.literal(true) });
export const updateProfileResponseSchema = updatedResponseSchema;
export const verifyEmailSendResponseSchema = z.object({
    sent: z.literal(true).optional(),
    alreadyVerified: z.literal(true).optional(),
});
export const verifyEmailConfirmResponseSchema = z.object({ verified: z.literal(true) });
export const prepareStaffInviteResponseSchema = z.object({ ticket: z.string() });
export const completeStaffInviteResponseSchema = z.object({ emailVerified: z.literal(true) });
export const sendPasswordResetResponseSchema = sentResponseSchema;
export const grantFlagResponseSchema = z.object({
    uid: z.string(),
    flags: z.array(userFlagSchema),
});
export const inviteAdminResponseSchema = z.object({
    invited: z.literal(true),
    uid: z.string(),
});
export const resendInviteResponseSchema = sentResponseSchema;
export const revokeAccessResponseSchema = z.object({ revoked: z.literal(true) });
export const verifyIdResponseSchema = z.object({ verified: z.literal(true) });
export const clearIdVerificationResponseSchema = z.object({ cleared: z.literal(true) });
export const updateCustomerProfileResponseSchema = updatedResponseSchema;
export const omniumOrdersResponseSchema = z.object({
    orders: z.array(firestoreDocument()),
    fetchedAt: z.number(),
    cached: z.boolean(),
});
export const omniumLinkResponseSchema = z.object({
    customerId: z.string(),
});
export const createCustomerNoteResponseSchema = z.object({ id: z.string() });
export const deleteCustomerNoteResponseSchema = deletedResponseSchema;
export const mergeCustomersResponseSchema = z.object({
    merged: z.literal(true),
    reassigned: z.record(z.string(), z.number()),
});
export const createWalkinCustomerResponseSchema = z.object({ uid: z.string() });
export const vippsPrefillConsumeResponseSchema = z.object({
    vippsLinkToken: z.string(),
    name: z.string(),
    phone: z.string(),
    email: z.string(),
    address: z.string(),
    postalCode: z.string(),
    city: z.string(),
    bankAccountNumber: z.string(),
    kontoEier: z.string(),
    emailVerified: z.boolean(),
});
